// components/QuarantineLeadModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  AlertCircle,
  Clock,
  Loader,
  Calendar,
  User,
  FileText,
  Shield,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const QuarantineLeadModal = ({ isOpen, onClose, onConfirm, leadName }) => {
  const { isDarkMode } = useTheme();
  const [quarantineType, setQuarantineType] = useState("contacted");
  const [months, setMonths] = useState("");
  const [weeks, setWeeks] = useState("");
  const [days, setDays] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalDays, setTotalDays] = useState(0);

  // Calculate total days whenever duration fields change
  useEffect(() => {
    const monthsInDays = (parseInt(months) || 0) * 30;
    const weeksInDays = (parseInt(weeks) || 0) * 7;
    const daysCount = parseInt(days) || 0;
    setTotalDays(monthsInDays + weeksInDays + daysCount);
  }, [months, weeks, days]);

  // Set default duration when quarantine type changes
  useEffect(() => {
    if (quarantineType === "contacted") {
      // Default: 4 weeks
      setMonths("");
      setWeeks("4");
      setDays("");
    } else {
      // Default: 1 week
      setMonths("");
      setWeeks("1");
      setDays("");
    }
  }, [quarantineType]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (totalDays === 0) {
      alert("Please set at least 1 day for quarantine duration");
      return;
    }

    setIsSubmitting(true);

    try {
      await onConfirm({
        quarantineType,
        customDuration: totalDays,
        reason: reason.trim() || null,
      });
      handleClose();
    } catch (error) {
      console.error("Error quarantining lead:", error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setQuarantineType("contacted");
      setMonths("");
      setWeeks("");
      setDays("");
      setReason("");
      setIsSubmitting(false);
      onClose();
    }
  };

  const getDurationDisplay = () => {
    const parts = [];
    if (parseInt(months)) parts.push(`${months} month${months > 1 ? "s" : ""}`);
    if (parseInt(weeks)) parts.push(`${weeks} week${weeks > 1 ? "s" : ""}`);
    if (parseInt(days)) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    return parts.join(", ") || "Not set";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div
        className={`rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border ${
          isDarkMode
            ? "bg-gradient-to-br from-[#0F1115] to-[#0A0C10] border-gray-800/50"
            : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 z-10 border-b p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#13161B] to-[#0F1115]"
              : "border-gray-200 bg-gradient-to-br from-white to-gray-50"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                  isDarkMode
                    ? "bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-600/30"
                    : "bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-600/30"
                }`}
              >
                <Shield size={20} className="text-white" />
              </div>
              <div>
                <h2
                  className={`text-lg font-bold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Quarantine Lead
                </h2>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {leadName || "This lead"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className={`p-2 rounded-xl transition-all duration-200 disabled:opacity-50 ${
                isDarkMode
                  ? "hover:bg-gray-800/50 text-gray-400 hover:text-white"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          {/* Info Box */}
          <div
            className={`mb-5 p-4 rounded-xl flex items-start gap-3 ${
              isDarkMode
                ? "bg-blue-500/10 border border-blue-500/50"
                : "bg-blue-50 border border-blue-200"
            }`}
          >
            <AlertCircle
              size={20}
              className={`flex-shrink-0 mt-0.5 ${
                isDarkMode ? "text-blue-400" : "text-blue-600"
              }`}
            />
            <div>
              <p
                className={`text-sm font-medium mb-1 ${
                  isDarkMode ? "text-blue-200" : "text-blue-800"
                }`}
              >
                What is quarantine?
              </p>
              <p
                className={`text-xs ${
                  isDarkMode ? "text-blue-300" : "text-blue-600"
                }`}
              >
                Quarantined leads are temporarily moved to Lead Pot and won't
                appear in active lead lists. They'll be automatically released
                after the quarantine period.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Contact Status */}
            <div>
              <label
                className={`block text-sm font-medium mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Contact Status <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {/* Contacted Option */}
                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    quarantineType === "contacted"
                      ? isDarkMode
                        ? "border-blue-500/50 bg-blue-500/10"
                        : "border-blue-500 bg-blue-50"
                      : isDarkMode
                        ? "border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50"
                        : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <input
                    type="radio"
                    name="quarantineType"
                    value="contacted"
                    checked={quarantineType === "contacted"}
                    onChange={(e) => setQuarantineType(e.target.value)}
                    className="sr-only"
                    disabled={isSubmitting}
                  />
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      quarantineType === "contacted"
                        ? "border-blue-500 bg-blue-500/20"
                        : isDarkMode
                          ? "border-gray-600"
                          : "border-gray-300"
                    }`}
                  >
                    {quarantineType === "contacted" && (
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Contacted
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          isDarkMode
                            ? "bg-blue-500/20 text-blue-300 border-blue-500/30"
                            : "bg-blue-100 text-blue-700 border-blue-200"
                        }`}
                      >
                        Default: 4 weeks
                      </span>
                    </div>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Lead has been contacted. Default quarantine is 4 weeks.
                    </p>
                  </div>
                </label>

                {/* Not Contacted Option */}
                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    quarantineType === "not_contacted"
                      ? isDarkMode
                        ? "border-blue-500/50 bg-blue-500/10"
                        : "border-blue-500 bg-blue-50"
                      : isDarkMode
                        ? "border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50"
                        : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <input
                    type="radio"
                    name="quarantineType"
                    value="not_contacted"
                    checked={quarantineType === "not_contacted"}
                    onChange={(e) => setQuarantineType(e.target.value)}
                    className="sr-only"
                    disabled={isSubmitting}
                  />
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      quarantineType === "not_contacted"
                        ? "border-blue-500 bg-blue-500/20"
                        : isDarkMode
                          ? "border-gray-600"
                          : "border-gray-300"
                    }`}
                  >
                    {quarantineType === "not_contacted" && (
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Not Contacted
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          isDarkMode
                            ? "bg-gray-700 text-gray-300 border-gray-600"
                            : "bg-gray-200 text-gray-600 border-gray-300"
                        }`}
                      >
                        Default: 1 week
                      </span>
                    </div>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Lead has not been contacted. Default quarantine is 1 week.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Duration Selection */}
            <div>
              <label
                className={`block text-sm font-medium mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Quarantine Duration <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {/* Months */}
                <div>
                  <label
                    className={`block text-xs mb-1.5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    Months
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="12"
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                    placeholder="0"
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2.5 border rounded-xl text-center focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-600"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                {/* Weeks */}
                <div>
                  <label
                    className={`block text-xs mb-1.5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    Weeks
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="52"
                    value={weeks}
                    onChange={(e) => setWeeks(e.target.value)}
                    placeholder="0"
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2.5 border rounded-xl text-center focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-600"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                {/* Days */}
                <div>
                  <label
                    className={`block text-xs mb-1.5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    placeholder="0"
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2.5 border rounded-xl text-center focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-600"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>
              </div>

              {/* Duration Summary */}
              <div
                className={`mt-3 p-4 border rounded-xl ${
                  isDarkMode
                    ? "bg-gray-800/30 border-gray-700/50"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Total Duration:
                  </span>
                  <div className="text-right">
                    <span
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {getDurationDisplay()}
                    </span>
                    {totalDays > 0 && (
                      <span
                        className={`text-xs block mt-0.5 ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        ({totalDays} day{totalDays !== 1 ? "s" : ""})
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p
                className={`text-xs mt-2 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Set a custom quarantine period using any combination of months,
                weeks, and days
              </p>
            </div>

            {/* Reason */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Reason <span className="text-gray-500">(optional)</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., 'Not responding to emails', 'Company not a good fit'"
                rows="3"
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 resize-none ${
                  isDarkMode
                    ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-600"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
              />
              <p
                className={`text-xs mt-1.5 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Help your team understand why this lead was quarantined
              </p>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gradient-to-br from-gray-50 to-white"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={`flex-1 px-5 py-3 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 hover:text-gray-900"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || totalDays === 0}
              className={`flex-1 px-5 py-3 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg ${
                isDarkMode
                  ? "shadow-blue-600/20 hover:shadow-blue-600/30"
                  : "shadow-blue-600/20 hover:shadow-blue-600/30"
              }`}
              style={{
                background: isDarkMode
                  ? "radial-gradient(50% 50% at 50% 50%, #3B82F6 0%, #2563EB 100%)"
                  : "radial-gradient(50% 50% at 50% 50%, #3B82F6 0%, #2563EB 100%)",
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Quarantining...</span>
                </>
              ) : (
                <>
                  <Shield size={18} />
                  <span>Quarantine Lead</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default QuarantineLeadModal;
